/**
 * The start gate — the one thing the 1990 original did not need and this port
 * does. `PS.EXE` booted from DOS straight into `TITLE.TTM`; a browser will not
 * let an `AudioContext` leave `suspended` until the page has had a real user
 * gesture, so an intro that auto-starts on route load plays silent and, worse,
 * starts sequencing before its assets have finished arriving. The gate turns
 * both problems into one deliberate step: hold on the black screen the intro
 * opens with anyway (frame 0 of `TITLE.TTM` is a silent preload that draws
 * nothing — see `docs/game/ui-reference/intro.md`), take any input as the
 * activation gesture, unlock audio, and only then start the clock.
 *
 * This module is the sequencing rule, as a pure reducer: no DOM, no timers, no
 * audio, so every path — first visit, revisit, skip, replay — is steppable in a
 * test. The route component owns the effects and reacts to phase changes.
 */

/**
 * `gate` waits for input; `arming` has the gesture and is waiting on the last
 * of the preload plus the audio unlock; `playing` is the intro running against
 * a started clock; `menu` is the Main Menu with the title theme carrying on.
 */
export type GatePhase = "gate" | "arming" | "playing" | "menu";

export interface GateState {
  readonly phase: GatePhase;
  /** The page has had a real user gesture, so audio is allowed to start. */
  readonly gestured: boolean;
  /** Artwork, fonts, palette, tick source and the intro's audio files are all in. */
  readonly assetsReady: boolean;
  /** The `AudioContext` has been created and resumed off that gesture. */
  readonly audioReady: boolean;
  /** The intro was cut short by input rather than running out. */
  readonly skipped: boolean;
}

export type GateEvent =
  | { readonly kind: "assetsReady" }
  | { readonly kind: "audioReady" }
  | { readonly kind: "gesture" }
  | { readonly kind: "introFinished" }
  | { readonly kind: "replayIntro" };

/**
 * `introWillPlay` is the once-per-session decision (`sessionStorage`). When the
 * intro is skipped by policy there is nothing to gate: the Main Menu is drawn
 * immediately and the title theme waits for whatever the player's first click
 * or keypress happens to be, exactly as it did before the gate existed.
 */
export function createGateState(introWillPlay: boolean): GateState {
  return {
    phase: introWillPlay ? "gate" : "menu",
    gestured: false,
    assetsReady: false,
    audioReady: false,
    skipped: false,
  };
}

/** Both halves of condition (b): nothing may tick until the preload and the unlock have both landed. */
export function gateReady(state: GateState): boolean {
  return state.assetsReady && state.audioReady;
}

/** `arming` is the only phase that can fall through on its own, and only when both readiness signals are in. */
function settle(state: GateState): GateState {
  return state.phase === "arming" && gateReady(state) ? { ...state, phase: "playing" } : state;
}

export function advanceGate(state: GateState, event: GateEvent): GateState {
  switch (event.kind) {
    case "assetsReady":
      return state.assetsReady ? state : settle({ ...state, assetsReady: true });
    case "audioReady":
      return state.audioReady ? state : settle({ ...state, audioReady: true });
    case "gesture":
      return gesture(state);
    case "introFinished":
      return state.phase === "playing" ? { ...state, phase: "menu" } : state;
    case "replayIntro":
      return state.phase === "menu" ? { ...state, phase: "playing", skipped: false } : state;
  }
}

/**
 * One rule for every input the page can receive. On the gate it is the
 * activation gesture; during the intro it is the original's skip (`Escape` —
 * or anything else — jumps straight to the Main Menu, confirmed live); in the
 * menu it is only ever the record that the page has been interacted with, so
 * the title theme may start.
 */
function gesture(state: GateState): GateState {
  if (state.phase === "gate") return settle({ ...state, gestured: true, phase: "arming" });
  if (state.phase === "playing") return { ...state, gestured: true, phase: "menu", skipped: true };
  return state.gestured ? state : { ...state, gestured: true };
}

/** The two phases that draw the gate screen rather than the intro or the menu. */
export function gateVisible(state: GateState): boolean {
  return state.phase === "gate" || state.phase === "arming";
}
